import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit, FiPrinter, FiSend, FiDollarSign,
  FiTrash2, FiMail, FiDownload, FiCheck, FiLink, FiShare2, FiX
} from 'react-icons/fi';
import axios from 'axios';
import { API_URL, API_BASE_URL } from '../config';
import { getAuthToken } from '../services/api';
import RelatedLetters from '../components/RelatedLetters';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companySettings, setCompanySettings] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [linkingEntries, setLinkingEntries] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_date: new Date().toLocaleDateString('en-CA'),
    payment_method: 'Check',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    loadInvoice();
    loadCompanySettings();
  }, [id]);

  const loadInvoice = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/invoices/${id}`, config);
      setInvoice(response.data);
      setPaymentData(prev => ({ ...prev, amount: response.data.balance_due || 0 }));
      setLoading(false);
    } catch (error) {
      console.error('Error loading invoice:', error);
      setLoading(false);
    }
  };

  const loadCompanySettings = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/company-settings`, config);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error loading company settings:', error);
    }
  };

  const handleSendInvoice = async () => {
    if (!window.confirm('Mark this invoice as sent?')) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/invoices/${id}/send`, {}, config);
      loadInvoice();
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Failed to send invoice');
    }
  };

  const handleShareInvoice = async () => {
    try {
      // Get or create a public share token
      const config = await getAuthConfig();
      const response = await axios.post(`${API_URL}/invoices/${id}/share-token`, {}, config);
      const shareToken = response.data.share_token;
      const publicUrl = `${window.location.origin}/api/public/invoice/${shareToken}`;

      if (navigator.share) {
        await navigator.share({
          title: `Invoice ${invoice.invoice_number}`,
          text: `Invoice ${invoice.invoice_number} from ${companySettings.company_name || 'North Georgia Surveying'} - Total: $${(invoice.total || 0).toFixed(2)}`,
          url: publicUrl
        });
      } else {
        await navigator.clipboard.writeText(publicUrl);
        alert('Invoice link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
        alert('Failed to generate share link');
      }
    }
  };

  const handleEmailInvoice = () => {
    // The invoice goes to the bill-to address plus every Billing contact on the client's
    // roster, so seed the compose box with all of them — the sender can edit the list.
    const recipients = (invoice.email_recipients && invoice.email_recipients.length)
      ? invoice.email_recipients
      : (invoice.bill_to_email ? [invoice.bill_to_email] : []);
    if (recipients.length === 0) {
      alert('No email address on file for this client. Add one in the invoice billing details.');
      return;
    }
    const companyName = companySettings.company_name || 'North Georgia Surveying';
    // bill_to_name is the person only when a company is also on file; otherwise it IS the company
    const firstName = invoice.bill_to_company ? ((invoice.bill_to_name || '').split(' ')[0] || '') : '';
    setEmailForm({
      to: recipients.join(', '),
      subject: `Invoice ${invoice.invoice_number} from ${companyName}`,
      message: `${firstName ? `Hi ${firstName},\n\n` : ''}Please find invoice ${invoice.invoice_number} below and attached as a PDF. Let us know if you have any questions.\n\nThank you,\n${companyName}`,
    });
    setShowEmailModal(true);
  };

  const handleSendInvoiceEmail = async () => {
    const list = emailForm.to.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
    if (list.length === 0) { alert('At least one recipient email is required'); return; }
    setSendingEmail(true);
    try {
      const config = await getAuthConfig();
      const res = await axios.post(`${API_URL}/invoices/${id}/email`, {
        to_emails: list,
        subject: emailForm.subject.trim(),
        message: emailForm.message,
      }, config);
      const actual = res.data?.sent_to || list;
      setShowEmailModal(false);
      alert(`Invoice sent to:\n\n${actual.map(r => `  • ${r}`).join('\n')}` +
        (res.data?.pdf_attached === false
          ? '\n\nThe PDF copy could not be generated, so this one went out with the invoice in the email body only.'
          : '\n\nA PDF copy was attached.'));
      loadInvoice();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send email');
    }
    setSendingEmail(false);
  };

  const handleLinkTimeEntries = async () => {
    setLinkingEntries(true);
    try {
      const config = await getAuthConfig();
      const response = await axios.post(`${API_URL}/invoices/${id}/link-time-entries`, {}, config);
      const linkedCount = response.data.linked_count || 0;
      if (linkedCount > 0) {
        alert(`Successfully linked ${linkedCount} time entries to this invoice.`);
      } else {
        alert('No matching time entries found to link. Time entries may already be linked or the line item dates don\'t match any uninvoiced entries.');
      }
      loadInvoice();
    } catch (error) {
      console.error('Error linking time entries:', error);
      alert(error.response?.data?.error || 'Failed to link time entries');
    } finally {
      setLinkingEntries(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/invoices/${id}/payments`, paymentData, config);
      setShowPaymentModal(false);
      setPaymentData({
        amount: 0,
        payment_date: new Date().toLocaleDateString('en-CA'),
        payment_method: 'Check',
        reference_number: '',
        notes: ''
      });
      loadInvoice();
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Failed to add payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/invoices/${id}/payments/${paymentId}`, config);
      loadInvoice();
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment');
    }
  };

  const handlePrint = async () => {
    // Convert logo to base64 first if it exists
    let logoBase64 = null;
    if (companySettings.company_logo) {
      try {
        const response = await fetch(`${API_BASE_URL}${companySettings.company_logo}`);
        const blob = await response.blob();
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading logo for print:', error);
      }
    }

    // Get print content and replace image src with base64 if available
    let printContent = printRef.current.innerHTML;
    if (logoBase64 && companySettings.company_logo) {
      printContent = printContent.replace(
        new RegExp(`src="${API_BASE_URL}${companySettings.company_logo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`, 'g'),
        `src="${logoBase64}"`
      );
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              padding: 40px;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
            }
            .invoice-header {
              display: flex;
              justify-content: space-between;
              margin-bottom: 40px;
            }
            .company-info {
              text-align: left;
            }
            .company-name {
              font-size: 24px;
              font-weight: bold;
              color: #1a1a1a;
              margin-bottom: 8px;
            }
            .company-details {
              color: #666;
              font-size: 14px;
              line-height: 1.5;
            }
            .invoice-title {
              text-align: right;
            }
            .invoice-title h1 {
              font-size: 36px;
              color: #2563eb;
              margin: 0 0 8px;
            }
            .invoice-number {
              font-size: 16px;
              color: #666;
            }
            .bill-to-section {
              display: flex;
              justify-content: space-between;
              margin-bottom: 40px;
            }
            .bill-to {
              flex: 1;
            }
            .bill-to h3 {
              color: #666;
              font-size: 12px;
              text-transform: uppercase;
              margin: 0 0 8px;
            }
            .bill-to-name {
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 4px;
            }
            .bill-to-details {
              color: #555;
              font-size: 14px;
              line-height: 1.5;
            }
            .invoice-meta {
              text-align: right;
            }
            .meta-row {
              margin-bottom: 8px;
            }
            .meta-label {
              color: #666;
              font-size: 12px;
              text-transform: uppercase;
            }
            .meta-value {
              font-size: 14px;
              font-weight: 500;
            }
            .line-items table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }
            .line-items th {
              background-color: #f3f4f6;
              padding: 12px;
              text-align: left;
              font-weight: 600;
              border-bottom: 2px solid #e5e7eb;
            }
            .line-items td {
              padding: 12px;
              border-bottom: 1px solid #e5e7eb;
            }
            .line-items .amount {
              text-align: right;
            }
            .totals {
              display: flex;
              justify-content: flex-end;
            }
            .totals-table {
              width: 280px;
            }
            .totals-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .totals-row.total {
              font-size: 18px;
              font-weight: 700;
              border-bottom: none;
              border-top: 2px solid #333;
              padding-top: 12px;
            }
            .notes {
              margin-top: 40px;
              padding: 20px;
              background-color: #f9fafb;
              border-radius: 8px;
            }
            .notes h3 {
              margin: 0 0 8px;
              color: #666;
              font-size: 12px;
              text-transform: uppercase;
            }
            .notes p {
              margin: 0;
              color: #555;
              font-size: 14px;
            }
            .status-badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 500;
            }
            .status-paid { background: #dcfce7; color: #166534; }
            .status-sent { background: #dbeafe; color: #1d4ed8; }
            .status-draft { background: #e5e7eb; color: #374151; }
            .status-overdue { background: #fee2e2; color: #dc2626; }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Wait for images to load before printing
    const images = printWindow.document.images;
    if (images.length > 0) {
      let loadedCount = 0;
      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount >= images.length) {
          printWindow.print();
        }
      };
      for (let img of images) {
        if (img.complete) {
          checkAllLoaded();
        } else {
          img.onload = checkAllLoaded;
          img.onerror = checkAllLoaded;
        }
      }
    } else {
      printWindow.print();
    }
  };

  const getStatusStyle = (status) => {
    const styles = {
      'Draft': { bg: '#e5e7eb', color: '#374151' },
      'Sent': { bg: '#dbeafe', color: '#1d4ed8' },
      'Viewed': { bg: '#e0e7ff', color: '#4338ca' },
      'Paid': { bg: '#dcfce7', color: '#166534' },
      'Overdue': { bg: '#fee2e2', color: '#dc2626' },
      'Cancelled': { bg: '#f3f4f6', color: '#6b7280' }
    };
    return styles[status] || styles['Draft'];
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading invoice...</div>;
  }

  if (!invoice) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Invoice not found</div>;
  }

  const statusStyle = getStatusStyle(invoice.status);

  // 'Sent' can mean two different things: we emailed it, or someone flipped the status
  // by hand. Spell out which, so a Sent badge is never read as "the client has it".
  const sentAt = invoice.last_sent_at ? new Date(invoice.last_sent_at + 'Z') : null;
  const sendNotice = !sentAt
    ? { text: 'Not emailed yet', color: '#6b7280' }
    : invoice.last_sent_method === 'marked'
      ? { text: `Marked sent ${sentAt.toLocaleString()} — not emailed`, color: '#b45309' }
      : invoice.last_sent_method === 'email'
        ? { text: `Emailed ${sentAt.toLocaleString()}`, color: '#6b7280' }
        : { text: `Sent ${sentAt.toLocaleString()} — delivery method not recorded`, color: '#6b7280' };

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header Actions */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/billing?tab=invoices'); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            marginBottom: '16px',
            padding: '0'
          }}
        >
          <FiArrowLeft /> Back to Billing
        </button>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h1 style={{ margin: 0, fontSize: '24px' }}>Invoice {invoice.invoice_number}</h1>
              <span style={{
                display: 'inline-block',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '500',
                backgroundColor: statusStyle.bg,
                color: statusStyle.color
              }}>
                {invoice.status}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: sendNotice.color, marginTop: '6px' }}>
              {sendNotice.text}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate(`/billing/invoices/${id}/edit`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <FiEdit size={16} /> Edit
            </button>

            <button
              onClick={handleLinkTimeEntries}
              disabled={linkingEntries}
              title="Link uninvoiced time entries to this invoice based on line item dates"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                backgroundColor: linkingEntries ? '#e5e7eb' : '#dbeafe',
                border: '1px solid #93c5fd',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: linkingEntries ? 'wait' : 'pointer',
                color: '#1d4ed8'
              }}
            >
              <FiLink size={16} /> {linkingEntries ? 'Linking...' : 'Link Time Entries'}
            </button>

            <button
              onClick={handlePrint}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <FiPrinter size={16} /> Print / PDF
            </button>

            <button
              onClick={handleShareInvoice}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                color: '#16a34a'
              }}
            >
              <FiShare2 size={16} /> Share
            </button>

            <button
              onClick={handleEmailInvoice}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                backgroundColor: '#eff6ff',
                border: '1px solid #93c5fd',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                color: '#2563eb'
              }}
            >
              <FiMail size={16} /> Email {invoice.bill_to_email ? '' : '(no email)'}
            </button>

            {invoice.status === 'Draft' && (
              <button
                onClick={handleSendInvoice}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <FiSend size={16} /> Mark as Sent
              </button>
            )}
            
            {invoice.balance_due > 0 && invoice.status !== 'Draft' && (
              <button
                onClick={() => {
                  setPaymentData(prev => ({ ...prev, amount: invoice.balance_due }));
                  setShowPaymentModal(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <FiDollarSign size={16} /> Record Payment
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Main Invoice Content */}
        <div 
          ref={printRef}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          {/* Invoice Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '40px' 
          }}>
            <div>
              {companySettings.company_logo && (
                <img 
                  src={`${API_BASE_URL}${companySettings.company_logo}`}
                  alt="Company Logo"
                  style={{ maxHeight: '70px', maxWidth: '180px', marginBottom: '12px', display: 'block' }}
                />
              )}
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '8px' }}>
                {companySettings.company_name || 'Your Company Name'}
              </div>
              <div style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                {companySettings.company_address && <div>{companySettings.company_address}</div>}
                {companySettings.company_city_state_zip && <div>{companySettings.company_city_state_zip}</div>}
                {companySettings.company_phone && <div>{companySettings.company_phone}</div>}
                {companySettings.company_email && <div>{companySettings.company_email}</div>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <h1 style={{ fontSize: '36px', color: '#2563eb', margin: '0 0 8px' }}>INVOICE</h1>
              <div style={{ fontSize: '16px', color: '#666' }}>{invoice.invoice_number}</div>
            </div>
          </div>

          {/* Bill To & Meta */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            marginBottom: '40px'
          }}>
            <div>
              <h3 style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase', margin: '0 0 8px' }}>
                Bill To
              </h3>
              {invoice.bill_to_company ? (
                <>
                  <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                    {invoice.bill_to_company}
                  </div>
                  <div style={{ color: '#555', fontSize: '14px', lineHeight: '1.6' }}>
                    {invoice.bill_to_name && <div>{invoice.bill_to_name}</div>}
                    {invoice.bill_to_address && <div>{invoice.bill_to_address}</div>}
                    {invoice.bill_to_city_state_zip && <div>{invoice.bill_to_city_state_zip}</div>}
                    {invoice.bill_to_email && <div>{invoice.bill_to_email}</div>}
                    {invoice.bill_to_phone && <div>{invoice.bill_to_phone}</div>}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                    {invoice.bill_to_name}
                  </div>
                  <div style={{ color: '#555', fontSize: '14px', lineHeight: '1.6' }}>
                    {invoice.bill_to_address && <div>{invoice.bill_to_address}</div>}
                    {invoice.bill_to_city_state_zip && <div>{invoice.bill_to_city_state_zip}</div>}
                    {invoice.bill_to_email && <div>{invoice.bill_to_email}</div>}
                    {invoice.bill_to_phone && <div>{invoice.bill_to_phone}</div>}
                  </div>
                </>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Invoice Date</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  {invoice.invoice_date ? new Date(invoice.invoice_date + 'T12:00:00').toLocaleDateString() : '—'}
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Due Date</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  {invoice.due_date ? new Date(invoice.due_date + 'T12:00:00').toLocaleDateString() : '—'}
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Terms</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>{invoice.terms || '—'}</div>
              </div>
              {invoice.po_number && (
                <div>
                  <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>P.O. Number</div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{invoice.po_number}</div>
                </div>
              )}
            </div>
          </div>

          {/* Line Items */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
            <thead>
              <tr>
                <th style={{ 
                  backgroundColor: '#f3f4f6', 
                  padding: '12px', 
                  textAlign: 'left', 
                  fontWeight: '600',
                  borderBottom: '2px solid #e5e7eb'
                }}>Description</th>
                <th style={{ 
                  backgroundColor: '#f3f4f6', 
                  padding: '12px', 
                  textAlign: 'center', 
                  fontWeight: '600',
                  borderBottom: '2px solid #e5e7eb',
                  width: '80px'
                }}>Qty</th>
                <th style={{ 
                  backgroundColor: '#f3f4f6', 
                  padding: '12px', 
                  textAlign: 'right', 
                  fontWeight: '600',
                  borderBottom: '2px solid #e5e7eb',
                  width: '100px'
                }}>Rate</th>
                <th style={{ 
                  backgroundColor: '#f3f4f6', 
                  padding: '12px', 
                  textAlign: 'right', 
                  fontWeight: '600',
                  borderBottom: '2px solid #e5e7eb',
                  width: '100px'
                }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items?.map((item, index) => (
                <tr key={index}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                    {item.description}
                    {item.job_number && (
                      <span style={{ 
                        marginLeft: '8px', 
                        fontSize: '12px', 
                        color: '#6b7280',
                        backgroundColor: '#f3f4f6',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        {item.job_number}
                      </span>
                    )}
                    {item.work_order_number && (
                      <span style={{ 
                        marginLeft: '8px', 
                        fontSize: '12px', 
                        color: '#7c3aed',
                        backgroundColor: '#f3e8ff',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        {item.work_order_number}
                        {item.work_order_description && `: ${item.work_order_description}`}
                        {item.work_order_po_number && ` (PO: ${item.work_order_po_number})`}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                    {item.quantity}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                    {formatCurrency(item.rate)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: '500' }}>
                    {formatCurrency(item.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: '280px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '8px 0',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <span>Subtotal</span>
                <span>{formatCurrency(invoice.subtotal)}</span>
              </div>
              {invoice.discount_amount > 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  padding: '8px 0',
                  borderBottom: '1px solid #e5e7eb',
                  color: '#dc2626'
                }}>
                  <span>Discount ({invoice.discount_percent}%)</span>
                  <span>-{formatCurrency(invoice.discount_amount)}</span>
                </div>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '12px 0',
                borderTop: '2px solid #333',
                marginTop: '8px',
                fontSize: '18px',
                fontWeight: '700'
              }}>
                <span>Total</span>
                <span>{formatCurrency(invoice.total)}</span>
              </div>
              {invoice.amount_paid > 0 && (
                <>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '8px 0',
                    color: '#16a34a'
                  }}>
                    <span>Amount Paid</span>
                    <span>-{formatCurrency(invoice.amount_paid)}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '12px 0',
                    borderTop: '1px solid #e5e7eb',
                    fontSize: '18px',
                    fontWeight: '700',
                    color: invoice.balance_due > 0 ? '#dc2626' : '#16a34a'
                  }}>
                    <span>Balance Due</span>
                    <span>{formatCurrency(invoice.balance_due)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div style={{ 
              marginTop: '40px', 
              padding: '20px', 
              backgroundColor: '#f9fafb', 
              borderRadius: '8px' 
            }}>
              <h3 style={{ margin: '0 0 8px', color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>
                Notes
              </h3>
              <p style={{ margin: 0, color: '#555', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                {invoice.notes}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Related Jobs */}
          {invoice.jobs?.length > 0 && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Related Jobs</h3>
              {invoice.jobs.map(job => (
                <div 
                  key={job.id}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                >
                  <div style={{ fontWeight: '500', marginBottom: '2px' }}>{job.job_number}</div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>{job.property_address}</div>
                </div>
              ))}
            </div>
          )}

          {/* Payment History */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Payment History</h3>
            {invoice.payments?.length > 0 ? (
              invoice.payments.map(payment => (
                <div 
                  key={payment.id}
                  style={{
                    padding: '12px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start' 
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px' }}>
                        {formatCurrency(payment.amount)}
                      </div>
                      <div style={{ fontSize: '13px', color: '#166534' }}>
                        {new Date(payment.payment_date + 'T12:00:00').toLocaleDateString()}
                        {payment.payment_method && ` • ${payment.payment_method}`}
                      </div>
                      {payment.reference_number && (
                        <div style={{ fontSize: '12px', color: '#166534', marginTop: '2px' }}>
                          Ref: {payment.reference_number}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeletePayment(payment.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#dc2626',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                      title="Delete payment"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ 
                padding: '20px', 
                textAlign: 'center', 
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                No payments recorded
              </div>
            )}
            
            {invoice.balance_due > 0 && invoice.status !== 'Draft' && (
              <button
                onClick={() => {
                  setPaymentData(prev => ({ ...prev, amount: invoice.balance_due }));
                  setShowPaymentModal(true);
                }}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '10px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <FiDollarSign size={16} /> Record Payment
              </button>
            )}
          </div>

          {/* Internal Notes */}
          {invoice.internal_notes && (
            <div style={{
              backgroundColor: '#fef3c7',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: '#92400e' }}>
                ⚠️ Internal Notes
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#92400e', whiteSpace: 'pre-wrap' }}>
                {invoice.internal_notes}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px' }}>Record Payment</h3>
            <form onSubmit={handleAddPayment}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Amount
                </label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  step="0.01"
                  min="0"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Payment Date
                </label>
                <input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Payment Method
                </label>
                <select
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payment_method: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="Check">Check</option>
                  <option value="Cash">Cash</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="ACH">ACH / Bank Transfer</option>
                  <option value="Wire">Wire Transfer</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Reference # (check number, transaction ID, etc.)
                </label>
                <input
                  type="text"
                  value={paymentData.reference_number}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="Optional"
                />
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Notes
                </label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="2"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder="Optional"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <FiCheck size={16} /> Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Compose the email that carries the invoice */}
      {showEmailModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            width: '100%', maxWidth: '600px', maxHeight: 'calc(90dvh - 90px)', overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: '14px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiMail color="#2563eb" /> Email Invoice {invoice.invoice_number}
              </h2>
              <button onClick={() => setShowEmailModal(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px',
              }}>
                <FiX size={20} />
              </button>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                To <span style={{ color: '#9ca3af', fontWeight: '400' }}>(separate multiple addresses with commas)</span>
              </label>
              <textarea
                value={emailForm.to}
                onChange={(e) => setEmailForm(p => ({ ...p, to: e.target.value }))}
                rows={2}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Subject</label>
              <input
                type="text"
                value={emailForm.subject}
                onChange={(e) => setEmailForm(p => ({ ...p, subject: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                Message <span style={{ color: '#9ca3af', fontWeight: '400' }}>(appears above the invoice)</span>
              </label>
              <textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm(p => ({ ...p, message: e.target.value }))}
                rows={8}
                placeholder="Add a personal note... (optional)"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            <div style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#f9fafb', padding: '10px 12px', borderRadius: '6px' }}>
              The full invoice (line items, totals, amount due) is included automatically below your message.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
              <button
                onClick={() => setShowEmailModal(false)}
                disabled={sendingEmail}
                style={{ padding: '10px 18px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvoiceEmail}
                disabled={sendingEmail || !emailForm.to.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: sendingEmail || !emailForm.to.trim() ? '#9ca3af' : '#2563eb',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: sendingEmail || !emailForm.to.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FiSend size={14} /> {sendingEmail ? 'Sending...' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Letters referencing this invoice */}
      <RelatedLetters relatedType="invoice" relatedId={parseInt(id, 10)} />
    </div>
  );
}

export default InvoiceView;